import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { journalAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { emptyForm } from './journal/journalConstants';
import JournalStats from './journal/JournalStats';
import TradeForm from './journal/TradeForm';
import CloseTradeForm from './journal/CloseTradeForm';
import TradesList from './journal/TradesList';

const buildPayload = (form) => ({
  symbol: form.symbol.toUpperCase(),
  side: form.side,
  tradeType: form.tradeType,
  size: form.size ? Number(form.size) : null,
  entryPrice: form.entryPrice ? Number(form.entryPrice) : null,
  entryDate: form.entryDate || null,
  exitPrice: form.exitPrice ? Number(form.exitPrice) : null,
  exitDate: form.exitDate || null,
  stopLoss: form.stopLoss ? Number(form.stopLoss) : null,
  takeProfit: form.takeProfit ? Number(form.takeProfit) : null,
  timeframe: form.timeframe,
  strategy: form.strategy || null,
  setup: form.setup || null,
  confidence: form.confidence ? Number(form.confidence) : null,
  fees: form.fees ? Number(form.fees) : null,
  notes: form.notes || null,
  thesis: form.thesis || null,
  mistakes: form.mistakes || null,
  lessons: form.lessons || null,
  emotions: form.emotions || null,
  tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
  status: form.status,
});

const formFromEntry = (entry) => ({
  symbol: entry.symbol || '',
  side: entry.side || 'buy',
  tradeType: entry.tradeType || 'stock',
  size: entry.size ?? '',
  entryPrice: entry.entryPrice ?? '',
  entryDate: entry.entryDate ? entry.entryDate.split('T')[0] : '',
  exitPrice: entry.exitPrice ?? '',
  exitDate: entry.exitDate ? entry.exitDate.split('T')[0] : '',
  stopLoss: entry.stopLoss ?? '',
  takeProfit: entry.takeProfit ?? '',
  timeframe: entry.timeframe || 'medium',
  strategy: entry.strategy || '',
  setup: entry.setup || '',
  confidence: entry.confidence ?? '',
  fees: entry.fees ?? '',
  notes: entry.notes || '',
  thesis: entry.thesis || '',
  mistakes: entry.mistakes || '',
  lessons: entry.lessons || '',
  emotions: entry.emotions || 'neutral',
  tags: entry.tags ? entry.tags.join(', ') : '',
  status: entry.status || 'open',
});

const Journal = () => {
  const { user, isPro, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('open');
  const [closingEntry, setClosingEntry] = useState(null);
  const [closeForm, setCloseForm] = useState({ exitPrice: '', mistakes: '', lessons: '' });

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await journalAPI.list();
      setEntries(data || []);
      const statsData = await journalAPI.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading journal entries', error);
      if (error?.response?.status === 403) {
        toast({
          title: 'Pro feature',
          description: 'Journaling is available for Pro users only.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user && isPro) loadEntries();
  }, [user, isPro, loadEntries]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.symbol.trim()) {
      toast({ title: 'Symbol required', variant: 'destructive' });
      return;
    }

    const payload = buildPayload(form);

    try {
      setLoading(true);
      if (editingId) {
        const updated = await journalAPI.update(editingId, payload);
        setEntries((prev) => prev.map((it) => (it.id === editingId ? updated : it)));
        toast({ title: 'Entry updated' });
      } else {
        const created = await journalAPI.create(payload);
        setEntries((prev) => [created, ...prev]);
        toast({ title: 'Entry added' });
      }
      resetForm();
      loadEntries();
    } catch (error) {
      console.error('Error saving entry', error);
      toast({ title: 'Error', description: 'Could not save entry.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setForm(formFromEntry(entry));
    setShowForm(true);
  };

  const handleStartClose = (entry) => {
    setClosingEntry(entry);
    setCloseForm({ exitPrice: '', mistakes: '', lessons: '' });
  };

  const submitClose = async () => {
    if (!closeForm.exitPrice) {
      toast({ title: 'Exit price required', variant: 'destructive' });
      return;
    }
    try {
      setLoading(true);
      const updated = await journalAPI.close(closingEntry.id, {
        exitPrice: Number(closeForm.exitPrice),
        exitDate: null,
        mistakes: closeForm.mistakes || null,
        lessons: closeForm.lessons || null,
      });
      setEntries((prev) => prev.map((it) => (it.id === closingEntry.id ? updated : it)));
      setClosingEntry(null);
      toast({ title: 'Trade closed' });
      loadEntries();
    } catch (error) {
      console.error('Error closing entry', error);
      toast({ title: 'Error', description: 'Could not close trade.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await journalAPI.remove(id);
      setEntries((prev) => prev.filter((it) => it.id !== id));
      toast({ title: 'Entry deleted' });
      loadEntries();
    } catch (error) {
      console.error('Error deleting entry', error);
      toast({ title: 'Error', description: 'Could not delete entry.', variant: 'destructive' });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle>Sign in to use the Journal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Please sign in to access the professional trading journal.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle>Pro Feature</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-2">
              Advanced trade journaling with P&L tracking, analytics, and institutional features is part of the Neon Alpha Pro subscription.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Trade Journal</h1>
            <p className="text-gray-500 text-sm">Track, analyze, and improve your trading performance</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-purple-600 text-white">Pro Feature</Badge>
            <Button
              onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="journal-new-trade-btn"
            >
              <Plus size={16} className="mr-2" />
              New Trade
            </Button>
          </div>
        </div>

        <JournalStats stats={stats} />

        {showForm && (
          <TradeForm
            form={form}
            editingId={editingId}
            loading={loading}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onCancel={resetForm}
          />
        )}

        <CloseTradeForm
          entry={closingEntry}
          closeForm={closeForm}
          loading={loading}
          onChange={setCloseForm}
          onSubmit={submitClose}
          onCancel={() => setClosingEntry(null)}
        />

        <TradesList
          entries={entries}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          loading={loading}
          onEdit={handleEdit}
          onClose={handleStartClose}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default Journal;
